import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CanActivateFn, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Dictation } from '../../_interfaces/dictation';
import { ApiService } from '../../_services/api.service';
import { openCopilotSnackbar } from '../../shared/components/copilot-snackbar/open-copilot-snackbar';

/** Outcome of the guard's single getDictation call, consumed by the document page. */
type PrefetchedDictation =
  | { ok: true; dictation: Dictation }
  | { ok: false; error: unknown };

let prefetched: { id: string; result: PrefetchedDictation } | null = null;

function rememberPrefetch(id: string, result: PrefetchedDictation): void {
  prefetched = { id, result };
}

function clearPrefetch(): void {
  prefetched = null;
}

/** Returns the guard's dictation result for this id, once. Null if the page must load it itself. */
export function takePrefetchedDictation(id: string): PrefetchedDictation | null {
  if (!prefetched) return null;
  if (prefetched.id !== id) {
    prefetched = null;
    return null;
  }
  const result = prefetched.result;
  prefetched = null;
  return result;
}

export const documentGuard: CanActivateFn = async (route) => {
  const apiService = inject(ApiService);
  const router = inject(Router);
  const snackBar = inject(MatSnackBar);
  const translate = inject(TranslateService);

  const id = route.paramMap.get('_id');
  if (!id) {
    clearPrefetch();
    openCopilotSnackbar(snackBar, translate.instant('Encounter not found'));
    return router.parseUrl('/encounters');
  }

  if (prefetched && prefetched.id !== id) {
    clearPrefetch();
  }

  try {
    const dictation = await apiService.getDictation(id);
    rememberPrefetch(id, { ok: true, dictation });
    return true;
  } catch (error) {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 404 || error.status === 500) {
        clearPrefetch();
        openCopilotSnackbar(snackBar, translate.instant('Encounter not found'));
        return router.parseUrl('/encounters');
      }
      if (error.status === 403) {
        clearPrefetch();
        snackBar.open(
          translate.instant('You do not have access to this consultation'),
          translate.instant('Close'),
          { duration: 3000 },
        );
        return router.parseUrl('/home');
      }
    }
    rememberPrefetch(id, { ok: false, error });
    return true;
  }
};
